import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useEvents } from '../hooks/useEvents';
import './ClosedDays.css';

const MONTH_NAMES = [
  'Januar','Februar','Marts','April','Maj','Juni',
  'Juli','August','September','Oktober','November','December'
];
const DAY_NAMES = ['Ma','Ti','On','To','Fr','Lø','Sø'];

const PALETTE = ['#E8799F','#7DCFCA','#F9B98A','#9B59B6','#3498DB','#2ECC71','#E67E22'];
function colorForId(id) { return PALETTE[id % PALETTE.length]; }

function isoDate(y, m, d) {
  return `${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
}

function getDaysInMonth(year, month) {
  return new Date(year, month + 1, 0).getDate();
}

function getStartOffset(year, month) {
  const day = new Date(year, month, 1).getDay();
  return (day + 6) % 7;
}

function parseDanishDate(str) {
  const [d, m, y] = str.split('.');
  return `${y}-${m}-${d}`;
}

function expandBooking(booking) {
  const startIso = parseDanishDate(booking.booking_date_start);
  const stopIso  = parseDanishDate(booking.booking_date_stop);
  const dates = [];
  const cur = new Date(startIso);
  const end = new Date(stopIso);
  while (cur <= end) {
    dates.push(cur.toISOString().slice(0, 10));
    cur.setDate(cur.getDate() + 1);
  }
  return dates;
}

function deduplicateSlots(slots) {
  const seen = new Map();
  for (const slot of slots) {
    const key = slot.event_id;
    if (!seen.has(key) || slot.date < seen.get(key).date) seen.set(key, slot);
  }
  return Array.from(seen.values());
}

function formatPrice(p) { return p ? `${p} kr.` : 'Gratis'; }

function CalendarMonth({ year, month, closedSet, eventMap, onEventClick }) {
  const days = getDaysInMonth(year, month);
  const offset = getStartOffset(year, month);
  const today = new Date();
  const todayStr = isoDate(today.getFullYear(), today.getMonth(), today.getDate());

  const cells = [];
  for (let i = 0; i < offset; i++) cells.push(null);
  for (let d = 1; d <= days; d++) cells.push(d);

  return (
    <div className="cal-month">
      <div className="cal-month-name">{MONTH_NAMES[month]}</div>
      <div className="cal-grid">
        {DAY_NAMES.map(d => (
          <div key={d} className="cal-day-header">{d}</div>
        ))}
        {cells.map((d, i) => {
          if (!d) return <div key={`e-${i}`} className="cal-cell empty" />;
          const iso = isoDate(year, month, d);
          const isClosed  = closedSet.has(iso);
          const isToday   = iso === todayStr;
          const evSlots   = eventMap[iso] || [];
          const hasEvent  = evSlots.length > 0;
          const evColor   = hasEvent ? colorForId(evSlots[0].event?.id ?? 0) : null;

          return (
            <div
              key={iso}
              className={[
                'cal-cell',
                isClosed  ? 'closed'      : '',
                isToday   ? 'today'       : '',
                hasEvent  ? 'has-event'   : '',
              ].filter(Boolean).join(' ')}
              style={hasEvent ? { '--ev-color': evColor } : undefined}
              title={isClosed ? 'Afdeling 1 lukket (udlejet)' : hasEvent ? evSlots[0].event?.name : ''}
              onClick={() => hasEvent && onEventClick(evSlots[0])}
            >
              {d}
              {isClosed && <span className="closed-dot" />}
              {hasEvent && !isClosed && <span className="event-dot" style={{ background: evColor }} />}
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function ClosedDays() {
  const [closedDates, setClosedDates] = useState([]);
  const [loading, setLoading] = useState(true);
  const { slots } = useEvents();
  const navigate = useNavigate();

  useEffect(() => {
    fetch('https://little-stars.dk/api/bookinger')
      .then(res => res.json())
      .then(data => {
        if (data.success && Array.isArray(data.bookinger)) {
          setClosedDates(data.bookinger.flatMap(expandBooking));
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const closedSet = new Set(closedDates);

  // Build event map: date → deduplicated slots
  const rawEventMap = {};
  for (const slot of slots) {
    if (!rawEventMap[slot.date]) rawEventMap[slot.date] = [];
    rawEventMap[slot.date].push(slot);
  }

  const now = new Date();
  const thisYear  = now.getFullYear();
  const thisMonth = now.getMonth();
  const todayStr  = now.toISOString().slice(0, 10);

  // Months to show: any month with closed dates or upcoming events
  const relevantMonths = new Set();
  for (const d of closedDates) relevantMonths.add(d.slice(0, 7));
  for (const slot of slots) {
    if (slot.date >= todayStr) relevantMonths.add(slot.date.slice(0, 7));
  }

  const months = [];
  for (let y = thisYear; y <= thisYear + 1; y++) {
    for (let m = 0; m < 12; m++) {
      if (y === thisYear && m < thisMonth) continue;
      const key = `${y}-${String(m+1).padStart(2,'0')}`;
      if (relevantMonths.has(key)) months.push({ year: y, month: m });
    }
  }

  // Upcoming events for cards below calendar
  const upcomingDedup = deduplicateSlots(
    slots.filter(s => s.date >= todayStr).sort((a, b) => a.date.localeCompare(b.date))
  );

  function handleEventClick(slot) {
    navigate('/events', { state: { openSlot: slot.id } });
  }

  return (
    <section className="closed-days-section">
      <div className="container">
        <div className="closed-days-header">
          <div>
            <div className="section-label">Afdeling 1 - Udlejningsdage</div>
            <h2 className="section-title">
              Tjek hvornår vi er <span className="gradient-text">udlejet</span>
            </h2>
            <p className="closed-days-sub">
              Dagen før en udlejning lukker afdeling 1 kl. 14, og på selve udlejningsdagen er afdeling 1 lukket hele dagen. <strong>Afdeling 2 har altid åbent</strong> - også ved udlejninger.
            </p>
          </div>
          <div className="closed-legend-group">
            <div className="closed-legend">
              <span className="legend-dot" /> Udlejet (afd. 1 lukket)
            </div>
            <div className="closed-legend">
              <span className="legend-dot legend-dot-event" /> Event
            </div>
          </div>
        </div>

        {loading ? (
          <p className="closed-days-loading">Henter bookinger…</p>
        ) : (
          <div className="cal-months-grid">
            {months.map(({ year, month }) => (
              <CalendarMonth
                key={`${year}-${month}`}
                year={year}
                month={month}
                closedSet={closedSet}
                eventMap={rawEventMap}
                onEventClick={handleEventClick}
              />
            ))}
          </div>
        )}

        {/* Event cards below calendar */}
        {upcomingDedup.length > 0 && (
          <div className="cal-events-section">
            <div className="cal-events-header">
              <div className="section-label">Kommende events</div>
            </div>
            <div className="cal-events-grid">
              {upcomingDedup.map(slot => {
                const ev = slot.event;
                const color = colorForId(ev?.id ?? 0);
                const avail = ev?.seats ? Math.max(0, ev.seats - (ev.signup_count ?? 0)) : null;
                const soldOut = avail === 0;
                const d = new Date(slot.date + 'T00:00:00');
                const dateLabel = d.toLocaleDateString('da-DK', { weekday: 'short', day: 'numeric', month: 'short' });

                return (
                  <div
                    key={slot.id}
                    className="cal-ev-card"
                    style={{ '--ev-color': color }}
                    onClick={() => handleEventClick(slot)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={e => e.key === 'Enter' && handleEventClick(slot)}
                  >
                    <div className="cal-ev-color-bar" style={{ background: color }} />
                    <div className="cal-ev-body">
                      <div className="cal-ev-top">
                        <span className="cal-ev-tag" style={{ color, borderColor: color + '44', background: color + '15' }}>
                          {ev?.event_group?.name?.trim() || 'Event'}
                        </span>
                        {soldOut && <span className="cal-ev-tag cal-ev-soldout">Fuldt booket</span>}
                      </div>
                      <h3 className="cal-ev-name">{ev?.name}</h3>
                      <div className="cal-ev-meta">
                        <span>📅 {dateLabel}</span>
                        <span>🕐 {slot.start_time}–{slot.end_time}</span>
                        <span>💰 {formatPrice(ev?.price)}</span>
                        {avail !== null && !soldOut && <span>👥 {avail} ledige</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
